import { useState, useEffect, useMemo } from 'react'
import {
  FileText,
  Eye,
  CheckCircle2,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Filter,
  DollarSign,
  Loader2,
} from 'lucide-react'
import api from '../lib/api'
import type { DashboardAnalyticsData } from '../lib/api'
import { useLocale } from '../contexts/LocaleContext'

// English month names
const MONTH_NAMES_EN: Record<string, string> = {
  '01': 'January',
  '02': 'February',
  '03': 'March',
  '04': 'April',
  '05': 'May',
  '06': 'June',
  '07': 'July',
  '08': 'August',
  '09': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December',
}

// Hebrew month names
const MONTH_NAMES_HE: Record<string, string> = {
  '01': 'ינואר',
  '02': 'פברואר',
  '03': 'מרץ',
  '04': 'אפריל',
  '05': 'מאי',
  '06': 'יוני',
  '07': 'יולי',
  '08': 'אוגוסט',
  '09': 'ספטמבר',
  '10': 'אוקטובר',
  '11': 'נובמבר',
  '12': 'דצמבר',
}

function getMonthName(monthKey: string, locale: 'en' | 'he'): string {
  // monthKey is like "2026-01"
  const monthNum = monthKey.split('-')[1]
  const names = locale === 'he' ? MONTH_NAMES_HE : MONTH_NAMES_EN
  return names[monthNum] || monthNum
}

interface DashboardAnalyticsProps {
  /** Fallback: compute stats from local proposals if API fails */
  proposals?: {
    id: string
    status: string
    total_price: number
    view_count: number
    created_at: string
    sent_at?: string
    viewed_at?: string
    signed_at?: string
  }[]
  /** If true, only show stats cards by default, charts are hidden/collapsible */
  simpleMode?: boolean
}

export default function DashboardAnalytics({ proposals = [], simpleMode = true }: DashboardAnalyticsProps) {
  const { tx, locale } = useLocale()
  const [analyticsData, setAnalyticsData] = useState<DashboardAnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [collapsed, setCollapsed] = useState(simpleMode)

  // Fetch analytics from backend
  useEffect(() => {
    let cancelled = false
    async function fetchAnalytics() {
      try {
        setLoading(true)
        const data = await api.getDashboardAnalytics()
        if (!cancelled) {
          setAnalyticsData(data)
        }
      } catch (err) {
        console.warn('Failed to fetch dashboard analytics, using local data:', err)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    fetchAnalytics()
    return () => { cancelled = true }
  }, [])

  // Compute local fallback data from proposals
  const localData = useMemo((): DashboardAnalyticsData => {
    const created = proposals.length
    const sent = proposals.filter(p => p.status !== 'draft').length
    const viewed = proposals.filter(p =>
      p.status === 'viewed' || p.status === 'signed' || (p.view_count && p.view_count > 0)
    ).length
    const signed = proposals.filter(p => p.status === 'signed' || p.signed_at).length
    const revenue = proposals
      .filter(p => p.status === 'signed' || p.signed_at)
      .reduce((sum, p) => sum + (p.total_price || 0), 0)

    // Build monthly data for last 6 months
    const now = new Date()
    const monthly: DashboardAnalyticsData['monthly'] = []
    for (let i = 5; i >= 0; i--) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 1)
      const monthKey = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}`

      const monthProposals = proposals.filter(p => {
        const d = new Date(p.created_at)
        return d >= monthStart && d < monthEnd
      })

      const monthSent = monthProposals.filter(p => p.status !== 'draft').length
      const monthViewed = monthProposals.filter(p =>
        p.status === 'viewed' || p.status === 'signed' || (p.view_count && p.view_count > 0)
      ).length
      const monthSigned = monthProposals.filter(p => p.status === 'signed' || p.signed_at).length
      const monthRevenue = monthProposals
        .filter(p => p.status === 'signed' || p.signed_at)
        .reduce((sum, p) => sum + (p.total_price || 0), 0)

      monthly.push({
        month: monthKey,
        sent: monthSent,
        viewed: monthViewed,
        signed: monthSigned,
        revenue: monthRevenue,
      })
    }

    // Previous month comparison
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const thisMonthProposals = proposals.filter(p => new Date(p.created_at) >= thisMonthStart)
    const lastMonthProposals = proposals.filter(p => {
      const d = new Date(p.created_at)
      return d >= lastMonthStart && d < thisMonthStart
    })

    const conversionRate = viewed > 0 ? Math.round((signed / viewed) * 1000) / 10 : 0
    const avgTimeToSign = 0 // Cannot calculate from frontend only

    return {
      totals: { created, sent, viewed, signed, revenue },
      monthly,
      conversion_rate: conversionRate,
      avg_time_to_sign: avgTimeToSign,
      previous_month: {
        created: lastMonthProposals.length,
        sent: lastMonthProposals.filter(p => p.status !== 'draft').length,
        viewed: lastMonthProposals.filter(p =>
          p.status === 'viewed' || p.status === 'signed' || (p.view_count && p.view_count > 0)
        ).length,
        signed: lastMonthProposals.filter(p => p.status === 'signed' || p.signed_at).length,
        revenue: lastMonthProposals
          .filter(p => p.status === 'signed' || p.signed_at)
          .reduce((sum, p) => sum + (p.total_price || 0), 0),
      },
      current_month: {
        created: thisMonthProposals.length,
        sent: thisMonthProposals.filter(p => p.status !== 'draft').length,
        viewed: thisMonthProposals.filter(p =>
          p.status === 'viewed' || p.status === 'signed' || (p.view_count && p.view_count > 0)
        ).length,
        signed: thisMonthProposals.filter(p => p.status === 'signed' || p.signed_at).length,
        revenue: thisMonthProposals
          .filter(p => p.status === 'signed' || p.signed_at)
          .reduce((sum, p) => sum + (p.total_price || 0), 0),
      },
    }
  }, [proposals])

  const [chartRange, setChartRange] = useState<'1' | '3' | '6'>('1')

  const handleRangeChange = (nextRange: '1' | '3' | '6') => {
    setChartRange(nextRange)
  }

  // Use API data if available, else fallback to local
  const data = analyticsData || localData

  // Filter monthly data based on range
  const filteredMonthly = useMemo(() => {
    if (!data?.monthly) return []
    const count = parseInt(chartRange)
    return data.monthly.slice(-count)
  }, [data, chartRange])

  // Trend calculations (current month vs previous month)
  const trends = useMemo(() => {
    const prev = data.previous_month
    const curr = data.current_month
    if (!prev || !curr) {
      return { created: 0, viewed: 0, signed: 0, revenue: 0 }
    }
    return {
      created: curr.created - prev.created,
      viewed: curr.viewed - prev.viewed,
      signed: curr.signed - prev.signed,
      revenue: curr.revenue - prev.revenue,
    }
  }, [data])

  // Don't show analytics if there are no proposals
  if (proposals.length === 0 && !analyticsData) {
    return null
  }

  return (
    <div className="px-4 lg:px-6 pt-4">
      {/* Stats Cards Row - Always Visible */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-4">
        <AnalyticsStatCard
          label={tx('Proposals Created', 'הצעות שנוצרו')}
          value={data.totals.created}
          icon={<FileText className="w-5 h-5" />}
          color="indigo"
          trend={trends.created}
          trendSuffix={tx('vs last month', 'לעומת חודש שעבר')}
          tooltip={tx('Total number of proposals you have created', 'מספר ההצעות הכולל שיצרת')}
        />
        <AnalyticsStatCard
          label={tx('Proposals Viewed', 'הצעות שנצפו')}
          value={data.totals.viewed}
          subtitle={data.totals.created > 0
            ? `${Math.round((data.totals.viewed / data.totals.created) * 100)}% ${tx('of total', 'מהסך הכל')}`
            : undefined
          }
          icon={<Eye className="w-5 h-5" />}
          color="cyan"
          trend={trends.viewed}
          trendSuffix={tx('vs last month', 'לעומת חודש שעבר')}
          tooltip={tx('Proposals that clients have opened and viewed', 'הצעות שלקוחות פתחו וצפו בהן')}
        />
        <AnalyticsStatCard
          label={tx('Proposals Signed', 'הצעות שנחתמו')}
          value={data.totals.signed}
          subtitle={data.totals.viewed > 0
            ? `${Math.round((data.totals.signed / data.totals.viewed) * 100)}% ${tx('conversion', 'המרה')}`
            : undefined
          }
          icon={<CheckCircle2 className="w-5 h-5" />}
          color="emerald"
          trend={trends.signed}
          trendSuffix={tx('vs last month', 'לעומת חודש שעבר')}
          tooltip={tx('Proposals that clients have signed and accepted', 'הצעות שלקוחות חתמו ואישרו')}
        />
        <AnalyticsStatCard
          label={tx('Conversion Rate', 'אחוז המרה')}
          value={`${data.conversion_rate}%`}
          icon={<TrendingUp className="w-5 h-5" />}
          color="amber"
          tooltip={tx('Percentage of viewed proposals that were signed', 'אחוז ההצעות שנצפו והפכו לחתומות')}
        />
        <AnalyticsStatCard
          label={tx('Revenue', 'הכנסות')}
          value={locale === 'he' ? `₪${data.totals.revenue.toLocaleString('he-IL')}` : `$${data.totals.revenue.toLocaleString('en-US')}`}
          icon={<DollarSign className="w-5 h-5" />}
          color="violet"
          trend={trends.revenue}
          trendPrefix={locale === 'he' ? '₪' : '$'}
          trendSuffix={tx('vs last month', 'לעומת חודש שעבר')}
          tooltip={tx('Total value of all signed proposals', 'הסכום הכולל של כל ההצעות שנחתמו')}
        />
      </div>

      {/* Toggle Header for Detailed Analytics */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-slate-700" />
          {tx('Business Overview & Data', 'סקירה עסקית ונתונים')}
        </h2>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-100"
        >
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
          <span>{collapsed ? tx('Show full details', 'הצג פרטים מלאים') : tx('Hide details', 'הסתר פרטים')}</span>
          {collapsed ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronUp className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Collapsible Content */}
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          collapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
        }`}
      >
        {/* Two Column Layout: Funnel + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Compact Conversion Funnel */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              {tx('Conversion Funnel', 'משפך המרה')}
            </h3>
            <CompactFunnel
              created={data.totals.created}
              sent={data.totals.sent}
              viewed={data.totals.viewed}
              signed={data.totals.signed}
              tx={tx}
            />
          </div>

          {/* Monthly Activity Bar Chart */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-slate-500" />
                {tx('Monthly Activity', 'פעילות חודשית')}
              </h3>
              <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                <button
                  onClick={() => handleRangeChange('1')}
                  className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                    chartRange === '1' ? 'bg-slate-50 text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tx('Month', 'חודש')}
                </button>
                <button
                  onClick={() => handleRangeChange('3')}
                  className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                    chartRange === '3' ? 'bg-slate-50 text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tx('3 Months', '3 חודשים')}
                </button>
                <button
                  onClick={() => handleRangeChange('6')}
                  className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                    chartRange === '6' ? 'bg-slate-50 text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tx('6 Months', '6 חודשים')}
                </button>
              </div>
            </div>
            <MonthlyBarChart monthly={filteredMonthly} locale={locale} tx={tx} />
          </div>
        </div>
      </div>
    </div>
  )
}

// =================== Sub-Components ===================

function AnalyticsStatCard({
  label,
  value,
  subtitle,
  icon,
  color,
  trend,
  trendPrefix = '',
  trendSuffix = 'vs last month',
  tooltip,
}: {
  label: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  color: 'indigo' | 'cyan' | 'emerald' | 'amber' | 'violet'
  trend?: number
  trendPrefix?: string
  trendSuffix?: string
  tooltip?: string
}) {
  const iconColors = {
    indigo: 'bg-slate-600',
    cyan: 'bg-cyan-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    violet: 'bg-emerald-600',
  }

  const bgColors = {
    indigo: 'bg-slate-50 border-slate-100',
    cyan: 'bg-cyan-50 border-cyan-100',
    emerald: 'bg-emerald-50 border-emerald-100',
    amber: 'bg-amber-50 border-amber-100',
    violet: 'bg-emerald-50 border-emerald-100',
  }

  return (
    <div className={`${bgColors[color]} rounded-2xl p-4 border group relative`} title={tooltip}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs lg:text-sm text-slate-500 mb-1 truncate">{label}</p>
          <p className="text-xl lg:text-2xl font-bold text-slate-800 truncate">{value}</p>
          {subtitle && (
            <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
          )}
          {trend !== undefined && trend !== 0 && (
            <div className={`flex items-center gap-0.5 mt-1 text-[11px] font-medium ${
              trend > 0 ? 'text-emerald-600' : 'text-rose-500'
            }`}>
              {trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            <span>{trendPrefix}{Math.abs(trend).toLocaleString('en-US')}</span>
            <span className="text-slate-400 font-normal">{trendSuffix}</span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl ${iconColors[color]} flex items-center justify-center text-white shadow-lg shrink-0`}>
          {icon}
        </div>
      </div>
    </div>
  )
}

function CompactFunnel({
  created,
  sent,
  viewed,
  signed,
  tx,
}: {
  created: number
  sent: number
  viewed: number
  signed: number
  tx: (en: string, he: string) => string
}) {
  const steps = [
    { label: tx('Created', 'נוצרו'), value: created, icon: <FileText className="w-5 h-5" />, color: 'bg-slate-100 text-slate-700' },
    { label: tx('Sent', 'נשלחו'), value: sent, icon: <TrendingUp className="w-5 h-5" />, color: 'bg-cyan-100 text-cyan-600' },
    { label: tx('Viewed', 'נצפו'), value: viewed, icon: <Eye className="w-5 h-5" />, color: 'bg-amber-100 text-amber-600' },
    { label: tx('Signed', 'נחתמו'), value: signed, icon: <CheckCircle2 className="w-5 h-5" />, color: 'bg-emerald-100 text-emerald-600' },
  ]

  return (
    <div className="grid grid-cols-4 gap-2">
      {steps.map((step) => (
        <div key={step.label}>
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-2.5 text-center">
            <div className={`w-9 h-9 rounded-lg mx-auto mb-2 flex items-center justify-center ${step.color}`}>
              {step.icon}
            </div>
            <div className="text-lg font-bold text-slate-800 leading-none">{step.value}</div>
            <div className="text-[11px] text-slate-500 mt-1">{step.label}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

function MonthlyBarChart({
  monthly,
  locale,
  tx,
}: {
  monthly: DashboardAnalyticsData['monthly']
  locale: 'en' | 'he'
  tx: (en: string, he: string) => string
}) {
  if (monthly.length === 0) return null

  // Current month view: compact horizontal comparison chart
  if (monthly.length === 1) {
    const m = monthly[0]
    const maxCurrent = Math.max(m.sent, m.viewed, m.signed, 1)
    const currentLabel = getMonthName(m.month, locale)

    const rows = [
      { key: 'sent', label: tx('Sent', 'נשלחו'), value: m.sent, color: 'bg-slate-500' },
      { key: 'viewed', label: tx('Viewed', 'נצפו'), value: m.viewed, color: 'bg-amber-400' },
      { key: 'signed', label: tx('Signed', 'נחתמו'), value: m.signed, color: 'bg-emerald-500' },
    ]

    return (
      <div className="space-y-3">
        <div className="text-xs text-slate-500">{currentLabel}</div>
        {rows.map((row) => (
          <div key={row.key} className="grid grid-cols-[56px_1fr_28px] items-center gap-2">
            <span className="text-xs text-slate-600">{row.label}</span>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${row.color} transition-all duration-500`}
                style={{ width: `${Math.round((row.value / maxCurrent) * 100)}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-700 text-left">{row.value}</span>
          </div>
        ))}
      </div>
    )
  }

  // Multi-month view: grouped bars
  const activeMonths = monthly.filter(m => (m.sent + m.viewed + m.signed) > 0)
  const chartMonths = activeMonths.length > 0 ? activeMonths : monthly.slice(-1)
  const maxValue = Math.max(...chartMonths.map(m => Math.max(m.sent, m.viewed, m.signed)), 1)

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 h-28">
        {chartMonths.map((month) => (
          <div
            key={month.month}
            className="flex-1 min-w-0 flex flex-col items-center gap-1"
            title={`${getMonthName(month.month, locale)}: ${tx('Sent', 'נשלחו')} ${month.sent}, ${tx('Viewed', 'נצפו')} ${month.viewed}, ${tx('Signed', 'נחתמו')} ${month.signed}`}
          >
            <div className="w-full h-28 flex items-end justify-center gap-1">
              <div className="w-2.5 rounded-t bg-slate-500" style={{ height: month.sent > 0 ? `${Math.max((month.sent / maxValue) * 100, 8)}%` : '0%' }} />
              <div className="w-2.5 rounded-t bg-amber-400" style={{ height: month.viewed > 0 ? `${Math.max((month.viewed / maxValue) * 100, 8)}%` : '0%' }} />
              <div className="w-2.5 rounded-t bg-emerald-500" style={{ height: month.signed > 0 ? `${Math.max((month.signed / maxValue) * 100, 8)}%` : '0%' }} />
            </div>
            <span className="text-[11px] text-slate-500">{getMonthName(month.month, locale)}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-4 text-xs text-slate-600">
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-slate-500" />{tx('Sent', 'נשלחו')}</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-amber-400" />{tx('Viewed', 'נצפו')}</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-emerald-500" />{tx('Signed', 'נחתמו')}</div>
      </div>
    </div>
  )
}
